import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Mail, Phone, Briefcase, Euro, Users } from "lucide-react";
import { fmtEUR, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const empty = {
  first_name: "", last_name: "", birth_date: "", phone: "", email: "", profession: "",
  employer: "", income: 0, id_document: "", work_contract: "",
  guarantor_name: "", guarantor_phone: "", guarantor_email: "",
};

export default function Locataires() {
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");

  const load = () => Promise.all([api.get("/tenants"), api.get("/payments")]).then(([t, p]) => {
    setItems(t.data); setPayments(p.data);
  });
  useEffect(() => { load(); }, []);

  const start = (t) => { setEditing(t); setForm(t ? {...empty, ...t} : empty); setOpen(true); };
  const save = async () => {
    const body = {...form, income: +form.income};
    if (!body.birth_date) delete body.birth_date;
    try {
      if (editing) { await api.put(`/tenants/${editing.id}`, {...body, id: editing.id}); toast.success("Locataire mis à jour"); }
      else { await api.post("/tenants", body); toast.success("Locataire créé"); }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (!confirm("Supprimer ce locataire ?")) return; await api.delete(`/tenants/${id}`); toast.success("Supprimé"); load(); };

  const filtered = items.filter(t =>
    !search || `${t.first_name} ${t.last_name} ${t.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const tenantBalance = (tid) => {
    const ps = payments.filter(p => p.tenant_id === tid);
    return ps.reduce((s, p) => s + (p.amount_due - (p.amount_paid || 0)), 0);
  };

  return (
    <div className="space-y-6" data-testid="locataires-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-overline">Locataires</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:'Outfit'}}>Annuaire des locataires</h1>
          <p className="text-sm text-zinc-500 mt-1">{items.length} locataire(s)</p>
        </div>
        <Button data-testid="new-tenant-btn" onClick={() => start(null)}><Plus className="w-4 h-4 mr-1.5" />Nouveau locataire</Button>
      </div>

      <Input placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-sm" />

      <Card className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-4 py-3">Locataire</th><th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Profession</th><th className="px-4 py-3">Revenus</th>
              <th className="px-4 py-3">Solde</th><th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => {
              const bal = tenantBalance(t.id);
              return (
                <tr key={t.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`tenant-row-${t.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.first_name} {t.last_name}</div>
                    {t.birth_date && <div className="text-xs text-zinc-500">Né(e) le {fmtDate(t.birth_date)}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {t.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {t.email}</div>}
                    {t.phone && <div className="flex items-center gap-1 text-zinc-500"><Phone className="w-3 h-3" /> {t.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div>{t.profession}</div>
                    <div className="text-zinc-500">{t.employer}</div>
                  </td>
                  <td className="px-4 py-3 mono">{fmtEUR(t.income)}</td>
                  <td className={`px-4 py-3 mono font-medium ${bal > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtEUR(bal)}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => start(t)} data-testid={`edit-tenant-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => del(t.id)}><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-zinc-500"><Users className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucun locataire.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifier le locataire" : "Nouveau locataire"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="ident">
            <TabsList className="grid grid-cols-3"><TabsTrigger value="ident">Identité</TabsTrigger><TabsTrigger value="pro">Professionnel</TabsTrigger><TabsTrigger value="guar">Garant</TabsTrigger></TabsList>
            <TabsContent value="ident" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Prénom *</Label><Input data-testid="tenant-first" value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} /></div>
                <div><Label>Nom *</Label><Input data-testid="tenant-last" value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({...form, birth_date: e.target.value})} /></div>
                <div><Label>Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Pièce d'identité (réf.)</Label><Input value={form.id_document} onChange={(e) => setForm({...form, id_document: e.target.value})} /></div>
            </TabsContent>
            <TabsContent value="pro" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Profession</Label><Input value={form.profession} onChange={(e) => setForm({...form, profession: e.target.value})} /></div>
                <div><Label>Employeur</Label><Input value={form.employer} onChange={(e) => setForm({...form, employer: e.target.value})} /></div>
              </div>
              <div><Label>Revenu mensuel (€)</Label><Input type="number" value={form.income} onChange={(e) => setForm({...form, income: e.target.value})} /></div>
              <div><Label>Contrat de travail (réf.)</Label><Input value={form.work_contract} onChange={(e) => setForm({...form, work_contract: e.target.value})} /></div>
            </TabsContent>
            <TabsContent value="guar" className="space-y-3 pt-3">
              <div><Label>Nom du garant</Label><Input value={form.guarantor_name} onChange={(e) => setForm({...form, guarantor_name: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Téléphone garant</Label><Input value={form.guarantor_phone} onChange={(e) => setForm({...form, guarantor_phone: e.target.value})} /></div>
                <div><Label>Email garant</Label><Input value={form.guarantor_email} onChange={(e) => setForm({...form, guarantor_email: e.target.value})} /></div>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="tenant-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
